/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import {
  Theme,
  ResolvedTheme,
  SystemResolvedTheme,
  ThemeContextType,
  DEFAULT_THEME_CONFIG,
  getThemeClassName,
  resolveTheme
} from '@/types/theme';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>('light');

  // Initialize theme from localStorage or system preference
  useEffect(() => {
    const savedTheme = localStorage.getItem(DEFAULT_THEME_CONFIG.storageKey) as Theme;
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

    const initialTheme = savedTheme || (systemPrefersDark ? 'dark' : 'light');

    setThemeState(initialTheme);
    applyTheme(initialTheme);
  }, []);

  // Apply theme to DOM and localStorage
  const applyTheme = (newTheme: Theme) => {
    const root = document.documentElement;

    // Remove existing theme classes
    root.classList.remove('light', 'dark', 'neo-brutal');

    // Resolve system theme if needed
    const effectiveTheme = resolveTheme(newTheme);

    // Add new theme class
    root.classList.add(getThemeClassName(effectiveTheme));

    // Save to localStorage
    localStorage.setItem(DEFAULT_THEME_CONFIG.storageKey, newTheme);
  };

  // Set specific theme
  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    applyTheme(newTheme);
  }, []);

  // Cycle through themes: light -> dark -> neo-brutal -> light
  const toggleTheme = () => {
    let newTheme: Theme;
    if (theme === 'light') {
      newTheme = 'dark';
    } else if (theme === 'dark') {
      newTheme = 'neo-brutal';
    } else if (theme === 'neo-brutal') {
      newTheme = 'light';
    } else {
      // If system, start with light
      newTheme = 'light';
    }
    setTheme(newTheme);
  };

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't set a preference
      const savedTheme = localStorage.getItem(DEFAULT_THEME_CONFIG.storageKey);
      if (!savedTheme) {
        const systemTheme = e.matches ? 'dark' : 'light';
        setTheme(systemTheme);
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [setTheme]);

  // Calculate resolved theme - memoized to prevent calling window.matchMedia() on every render
  const resolvedTheme: ResolvedTheme = useMemo(() => {
    return resolveTheme(theme);
  }, [theme]);

  const value: ThemeContextType = {
    theme,
    resolvedTheme,
    isDark: resolvedTheme === 'dark',
    isSystem: theme === 'system',
    isNeoBrutal: resolvedTheme === 'neo-brutal',
    toggleTheme,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Custom hook to access theme context
 * 
 * @example
 * const { theme, isDark, toggleTheme } = useTheme();
 * 
 * @throws {Error} If used outside of ThemeProvider
 */
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};

